import { messageToWebkit } from "features/render/utils";
import {
    EventEmissionOptions,
    EventSubscriptionOptions,
    WebviewerEventCallback,
    WebviewerEventMap,
    WebviewerEventName,
} from "types/events";

interface Subscription<E extends WebviewerEventName> {
    callback: WebviewerEventCallback<E>;
    options: EventSubscriptionOptions;
}

export interface EventEmitterConfig {
    /**
     * Default behavior for whether to stop executing callbacks on error.
     * Can be overridden per callback or per emission.
     * @default false
     */
    stopOnErrorDefault?: boolean;
    /**
     * Whether to publish events to WebKit.
     * Enable this only for the public webviewer.eventEmitter to avoid duplicate messages.
     * @default false
     */
    publishToWebkit?: boolean;
}

/**
 * EventEmitter for Webviewer
 * Handles event subscription, unsubscription, and emission with type safety.
 * Supports both one-time and recurring events.
 */
export class EventEmitter {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly listeners = new Map<WebviewerEventName, Set<Subscription<any>>>();
    private readonly oneTimeEvents = new Set<WebviewerEventName>(["viewerReady", "viewerTerminated"]);
    private readonly firedOneTimeEvents = new Set<WebviewerEventName>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly oneTimeEventPayloads = new Map<WebviewerEventName, any>();
    private readonly config: EventEmitterConfig;

    constructor(config: EventEmitterConfig = {}) {
        this.config = {
            stopOnErrorDefault: false,
            publishToWebkit: false,
            ...config,
        };
    }

    /**
     * Subscribe to an event
     * @param eventName - The name of the event to subscribe to
     * @param callback - The callback function to invoke when the event is emitted
     * @param options - Optional subscription options
     * @returns A cleanup function to unsubscribe
     */
    public on<E extends WebviewerEventName>(
        eventName: E,
        callback: WebviewerEventCallback<E>,
        options: EventSubscriptionOptions = {},
    ): () => void {
        const subscription: Subscription<E> = {
            callback,
            options: {
                stopOnError: options.stopOnError,
            },
        };

        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }

        this.listeners.get(eventName)!.add(subscription);

        // Replay one-time events that have already fired
        if (this.oneTimeEvents.has(eventName) && this.firedOneTimeEvents.has(eventName)) {
            const lastPayload = this.oneTimeEventPayloads.get(eventName);
            if (lastPayload !== undefined) {
                queueMicrotask(() => {
                    try {
                        Promise.resolve(subscription.callback(lastPayload)).catch((error) => {
                            console.error(`Error in '${eventName}' event callback:`, error);
                        });
                    } catch (error) {
                        // Handle synchronous errors
                        console.error(`Error in '${eventName}' event callback:`, error);
                    }
                });
            }
        }

        // Return cleanup function
        return () => this.off(eventName, callback);
    }

    /**
     * Unsubscribe from an event
     * @param eventName - The name of the event to unsubscribe from
     * @param callback - The callback function to remove
     */
    public off<E extends WebviewerEventName>(eventName: E, callback: WebviewerEventCallback<E>): void {
        const eventListeners = this.listeners.get(eventName);
        if (!eventListeners) {
            return;
        }

        // Find and remove subscription with matching callback
        for (const subscription of eventListeners) {
            if (subscription.callback === callback) {
                eventListeners.delete(subscription);
                break;
            }
        }

        // Clean up empty listener sets
        if (eventListeners.size === 0) {
            this.listeners.delete(eventName);
        }
    }

    /**
     * Emit an event (internal use)
     * Invokes all registered callbacks asynchronously in registration order
     * @param eventName - The name of the event to emit
     * @param payload - The event payload
     * @param options - Optional emission options
     */
    public async emit<E extends WebviewerEventName>(
        eventName: E,
        payload: WebviewerEventMap[E],
        options: EventEmissionOptions = {},
    ): Promise<void> {
        // Mark one-time events as fired and store payload for replay
        // This must happen even if there are no listeners yet
        if (this.oneTimeEvents.has(eventName)) {
            this.firedOneTimeEvents.add(eventName);
            this.oneTimeEventPayloads.set(eventName, payload);
        }

        // Send message to webkit only if enabled. This always sends the event
        // because we have no listener registration implemented for this webkit
        // interface.
        if (this.config.publishToWebkit) {
            messageToWebkit(eventName, payload);
        }

        const eventListeners = this.listeners.get(eventName);
        if (!eventListeners || eventListeners.size === 0) {
            return;
        }

        // Execute callbacks in registration order (Set maintains insertion order)
        // NOTE: Copy listeners to avoid issues if the set is mutated (e.g. cleared during termination) while emitting
        for (const subscription of Array.from(eventListeners)) {
            try {
                // Always invoke asynchronously
                await subscription.callback(payload);
            } catch (error) {
                // Always log errors
                console.error(`Error in '${eventName}' event callback:`, error);

                // Determine if we should stop
                // Priority: subscription-level > emission-level > global default
                const shouldStop =
                    subscription.options.stopOnError ?? options.stopOnError ?? this.config.stopOnErrorDefault;
                if (shouldStop) {
                    // Stop execution but don't propagate
                    break;
                }
                // Otherwise continue to the next callback (swallow error)
            }
        }
    }

    /**
     * Check if an event has any listeners
     * @param eventName - The name of the event to check
     * @returns True if there are any listeners for the event, false otherwise
     */
    public hasListeners<E extends WebviewerEventName>(eventName: E): boolean {
        const eventListeners = this.listeners.get(eventName);
        return eventListeners !== undefined && eventListeners.size > 0;
    }

    /**
     * Reset the event emitter state
     * Clears all listeners and resets one-time event tracking.
     * Note: This does not invoke any cleanup callbacks - subscribers are simply removed.
     * Typically called during Webviewer termination.
     */
    public reset(): void {
        this.listeners.clear();
        this.firedOneTimeEvents.clear();
        this.oneTimeEventPayloads.clear();
    }
}
